import { createGrpcWebTransport } from '@connectrpc/connect-web';
import type { Interceptor, Transport } from '@connectrpc/connect';
import { webLog } from './webLogger';
import { makeTraceparent } from '../traceparent';
import { getGrpcBaseUrl, setGrpcPortValue } from './grpcRuntime';
import { grpcRecorderInterceptor } from '../../../testRecords/recording';

// Port resolution lives in the dependency-free leaf module ./grpcRuntime so the
// cyclic consumers (webLogger, useAuthStore, revitClient) can read the port
// without importing this module (which pulls in the auth store + logger
// interceptors). Re-exported here so existing `from '.../grpc/transport'`
// imports keep working unchanged.
export { getGrpcPort, isGrpcRuntimeAvailable, getGrpcBaseUrl } from './grpcRuntime';

let transport: Transport | null = null;

/**
 * Set (or clear) the active gRPC port. Delegates persistence to the leaf module
 * and additionally invalidates the cached Transport so the next
 * getGrpcTransport() rebuilds against the new base url.
 */
export function setGrpcPort(port: number | null): void {
  setGrpcPortValue(port);
  transport = null;
}

/**
 * Drops the cached Transport so the next getGrpcTransport() builds a fresh one.
 * Used by the operation poll loop after repeated call failures — a wedged
 * connection otherwise fails every subsequent RPC through the singleton.
 */
export function resetGrpcTransport(): void {
  transport = null;
}

// Traceparent interceptor: stamps every outbound Connect-RPC call with a
// W3C `traceparent` header so the plugin-side AspNetCore-instrumentation
// (see ObservabilityBootstrap.BuildTracerProvider) continues the trace
// instead of starting a fresh root. F4 of epic
// 2026-05-26_export-flow-and-observability, FR-1. Order in the
// interceptors[] array matters: this MUST run before loggingInterceptor
// so the log record can include the trace-id we just minted.
//
// We deliberately do NOT consult the OpenTelemetry browser SDK here —
// the SDK is a non-trivial bundle weight and the epic explicitly defers
// browser-side spans (per §3.3 of the epic README). Header-only
// propagation is enough: Tempo reconstructs the React "virtual" span
// from the plugin's parent_span_id.
export const traceparentInterceptor: Interceptor = (next) => async (req) => {
  if (!req.header.has('traceparent')) {
    req.header.set('traceparent', makeTraceparent());
  }
  return next(req);
};

// Attach only the opaque internal user id required for correlated plugin
// telemetry. Display identities, organization names, model names, and paths
// must never cross this local transport boundary or reach persisted logs.
//
// transport must NOT import useAuthStore — that closed the
// revitClient -> transport -> useAuthStore -> revitClient cycle
// (useAuthStore imports connectRevitGrpc from revitClient, revitClient imports
// getGrpcTransport from here). Instead useAuthStore registers a snapshot getter
// at module load via setUserHeaderSource. Until it does, anonymous requests
// stamp no user header.
type UserHeaderSnapshot = { id?: string } | null | undefined;
let userHeaderSource: () => UserHeaderSnapshot = () => null;

export function setUserHeaderSource(source: () => UserHeaderSnapshot): void {
  userHeaderSource = source;
}

export const userHeadersInterceptor: Interceptor = (next) => async (req) => {
  const user = userHeaderSource();
  if (user?.id && !req.header.has('x-pipeex-user-id')) {
    req.header.set('x-pipeex-user-id', user.id);
  }
  req.header.delete('x-pipeex-user-login');
  req.header.delete('x-pipeex-org-name');
  req.header.delete('x-pipeex-model-name');
  req.header.delete('x-pipeex-model-path');
  return next(req);
};

// Logging interceptor: every gRPC unary or server-streaming call is logged
// on send (request leaves browser) and on receive (response arrives). Both
// events go through webLog → POST /api/log → plugin Serilog → OTLP → Loki,
// giving a single distributed-trace stream where one Loki query shows the
// whole journey: click in UI → outgoing gRPC → plugin handler entry
// (GrpcExceptionInterceptor) → plugin handler exit → response on client.
const loggingInterceptor: Interceptor = (next) => async (req) => {
  const method = `${req.service.typeName}/${req.method.name}`;
  const start = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  webLog.info('grpc-client', `[gRPC.send] method=${method}`);
  try {
    const res = await next(req);
    const elapsedMs = Math.round(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - start) * 10) / 10;
    webLog.info('grpc-client', `[gRPC.recv] method=${method} status=OK elapsedMs=${elapsedMs}`);
    return res;
  } catch (err) {
    const elapsedMs = Math.round(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - start) * 10) / 10;
    const msg = err instanceof Error ? err.message : String(err);
    webLog.warn('grpc-client', `[gRPC.recv] method=${method} status=ERROR elapsedMs=${elapsedMs} detail=${msg}`);
    throw err;
  }
};

export function getGrpcTransport(): Transport | null {
  if (transport) {
    return transport;
  }

  const baseUrl = getGrpcBaseUrl();
  if (!baseUrl) {
    return null;
  }

  transport = createGrpcWebTransport({
    baseUrl,
    // Order matters: traceparentInterceptor first so subsequent
    // interceptors observe the just-stamped header. userHeadersInterceptor
    // (F5) runs next so loggingInterceptor's [gRPC.send] line carries
    // both the trace context and the user headers in the same record.
    // grpcRecorderInterceptor is innermost so it observes the final outgoing
    // message and the real response/error. It is a zero-overhead pass-through
    // until a recording session registers a sink (Test-Records epic).
    interceptors: [
      traceparentInterceptor,
      userHeadersInterceptor,
      loggingInterceptor,
      grpcRecorderInterceptor,
    ],
  });

  return transport;
}
